// VegNonVegSales.jsx
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import LoadingOverlay from './LoadingOverlay';
import { UserContext } from './UserContext';
import { API_ENDPOINTS } from '../config/api';

const VegNonVegSales = () => {
    const [filter, setFilter] = useState("overall"); // Options: today, yesterday, week, month, overall
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [data, setData] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const context = useContext(UserContext);
    const { user, restaurant } = context || {};
    const restaurantId = restaurant?._id || user?.id;

    const fetchData = async () => {
        if (!restaurantId) return;
        try {
            setIsLoading(true);
            let url = `${API_ENDPOINTS.ANALYTICS_VEG_NONVEG(restaurantId)}?period=${filter}`;
            if (customStart && customEnd) {
                url = `${API_ENDPOINTS.ANALYTICS_VEG_NONVEG(restaurantId)}?startDate=${customStart}&endDate=${customEnd}`;
            }
            const response = await axios.get(url);
            setData(response.data);

            // Prepare chart data
            const labels = response.data.map(item => item._id);
            const quantities = response.data.map(item => item.totalQuantity);
            const revenues = response.data.map(item => item.totalRevenue);

            setChartData({
                labels,
                datasets: [
                    {
                        label: "Total quantity",
                        data: quantities,
                        backgroundColor: "rgba(34, 197, 94, 0.75)", // emerald
                        borderColor: "rgba(22, 163, 74, 1)",
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        maxBarThickness: 32,
                        yAxisID: 'y',
                    },
                    {
                        label: "Total revenue (₹)",
                        data: revenues,
                        backgroundColor: "rgba(249, 115, 22, 0.75)", // orange
                        borderColor: "rgba(248, 150, 59, 1)",
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        maxBarThickness: 32,
                        yAxisID: 'y1',
                    },
                ],
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching veg/nonveg sales data:", error);
        }
    };

    useEffect(() => {
        if (restaurantId) {
            fetchData();
        }
    }, [filter, customStart, customEnd, restaurantId]);

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <div className="p-2 sm:p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Veg vs Non-veg mix</p>
                    <button
                        onClick={fetchData}
                        className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                    >
                        Refresh
                    </button>
                </div>
                <div className="flex flex-wrap items-end gap-2 mb-1 text-[11px]">
                    <div className="flex flex-col gap-0.5 flex-1 min-w-[90px] max-w-[130px]">
                        <span className="text-[9px] text-stone-500 font-semibold uppercase tracking-wide">Period</span>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="border border-stone-300 rounded-md px-2 py-1 text-[10px] bg-white w-full"
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="overall">Overall</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-0.5 flex-1 min-w-[90px] max-w-[130px]">
                        <span className="text-[9px] text-stone-500 font-semibold uppercase tracking-wide">Start</span>
                        <input
                            type="date"
                            value={customStart}
                            onChange={(e) => setCustomStart(e.target.value)}
                            className="border border-stone-300 rounded-md px-1.5 py-1 text-[10px] w-full"
                        />
                    </div>
                    <div className="flex flex-col gap-0.5 flex-1 min-w-[90px] max-w-[130px]">
                        <span className="text-[9px] text-stone-500 font-semibold uppercase tracking-wide">End</span>
                        <input
                            type="date"
                            value={customEnd}
                            onChange={(e) => setCustomEnd(e.target.value)}
                            className="border border-stone-300 rounded-md px-1.5 py-1 text-[10px] w-full"
                        />
                    </div>
                </div>
                {chartData && (
                    <div className="mb-2" style={{ height: 220 }}>
                        <Bar
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom', labels: { boxWidth: 8, font: { size: 10 } } },
                                    title: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => {
                                                const value = context.parsed.y || 0;
                                                const label = context.dataset.label || '';
                                                if (label.toLowerCase().includes('revenue')) {
                                                    return `${label}: ₹${value.toLocaleString('en-IN')}`;
                                                }
                                                return `${label}: ${value.toLocaleString('en-IN')}`;
                                            },
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        ticks: { font: { size: 9 } },
                                        grid: { display: false },
                                    },
                                    y: {
                                        type: 'linear',
                                        display: true,
                                        position: 'left',
                                        ticks: { font: { size: 9 } },
                                        title: { display: true, text: 'Quantity' },
                                        grid: {
                                            color: 'rgba(148, 163, 184, 0.2)',
                                            drawBorder: false,
                                            borderDash: [4, 4],
                                        },
                                    },
                                    y1: {
                                        type: 'linear',
                                        display: true,
                                        position: 'right',
                                        ticks: {
                                            font: { size: 9 },
                                            callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
                                        },
                                        title: { display: true, text: 'Revenue (₹)' },
                                        grid: { drawOnChartArea: false },
                                    },
                                },
                            }}
                        />
                    </div>
                )}
                <table className="min-w-full border text-[11px]">
                    <thead>
                        <tr>
                            <th className="border p-2">Type</th>
                            <th className="border p-2">Total Quantity</th>
                            <th className="border p-2">Total Revenue (Rs.)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, idx) => (
                            <tr key={idx}>
                                <td className="border p-2">{item._id}</td>
                                <td className="border p-2">{item.totalQuantity}</td>
                                <td className="border p-2">{item.totalRevenue.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default VegNonVegSales;
